import React from 'react'
import { CornerDownRight } from 'lucide-react'
import { mergeClasses } from '../../helpers/styling'
import { SvgIcon } from '../svg-icon'
import { CodeWidgetAnalysis, EgressTarget } from '../widgets/CodeWidget/analysis/code-widget-analysis'
import { STATUS_STYLE } from './status-style'

/**
 * A line number that jumps the source pane to it. Shaped as a button rather than tinted text: these sit
 * among plenty of other numbers, and nothing about a grey numeral says it does something when clicked.
 */
export function LineLink(props: { line: number; active?: boolean; onGoToLine: (line: number) => void }) {
  return (
    <button
      type="button"
      title={`Go to line ${props.line} in the source`}
      onClick={() => props.onGoToLine(props.line)}
      className={mergeClasses(
        'inline-flex items-center gap-0.5 rounded-sm border border-primary/30 bg-primary/10 px-1.5 py-0.5 font-mono text-xs text-primary transition-colors hover:border-primary/60 hover:bg-primary/20',
        { 'border-primary bg-primary text-primary-foreground': props.active }
      )}
    >
      <SvgIcon icon={CornerDownRight} size={11} className="shrink-0 opacity-70" />
      {props.line}
    </button>
  )
}

export function BridgeUsage(props: { widget: CodeWidgetAnalysis; onGoToLine: (line: number) => void }) {
  const used = props.widget.bridgeUsage.filter((entry) => entry.used)
  const unused = props.widget.bridgeUsage.filter((entry) => !entry.used)

  if (used.length === 0) return <p className="text-muted-foreground">This page never calls the host.</p>

  return (
    <>
      <ul className="space-y-1">
        {used.map((entry) => (
          <li
            key={entry.member}
            /* The whole row, not just the name: these are the calls that read or change stored data. */
            className={mergeClasses('flex gap-2 rounded-sm px-1', {
              [STATUS_STYLE.attention.row]: entry.reachesBackend,
            })}
          >
            <code
              className={mergeClasses('shrink-0 font-mono text-xs', {
                [STATUS_STYLE.attention.rowCode]: entry.reachesBackend,
              })}
            >
              wistor.{entry.member}
            </code>
            <span
              className={mergeClasses('text-muted-foreground', {
                [STATUS_STYLE.attention.rowText]: entry.reachesBackend,
              })}
            >
              {entry.description}
            </span>
            <span className="ml-auto flex shrink-0 flex-wrap items-center justify-end gap-0.5 text-xs text-muted-foreground">
              {entry.occurrences}&times; on
              {entry.evidence.map((item) => (
                <LineLink key={item.line} line={item.line} onGoToLine={props.onGoToLine} />
              ))}
            </span>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-xs text-muted-foreground">Unused: {unused.map((entry) => entry.member).join(', ')}</p>
    </>
  )
}

const URL_CLASS_LABEL: Record<EgressTarget['urlClass'], string> = {
  remote: 'external',
  relative: 'same origin',
  'same-origin': 'this site, in place',
  inline: 'inline data',
  dynamic: 'computed',
}

export function EgressTable(props: { targets: EgressTarget[]; onGoToLine: (line: number) => void }) {
  return (
    <table className="w-full table-fixed">
      <tbody>
        {props.targets.map((target, index) => (
          <tr key={`${target.kind}-${target.evidence.line}-${index}`} className="align-top">
            <td className="w-28 py-0.5 font-mono text-xs text-muted-foreground">{target.kind}</td>
            <td className="w-24 py-0.5 text-xs">
              <span
                className={mergeClasses('text-muted-foreground', {
                  [STATUS_STYLE.attention.text]: target.urlClass === 'remote' || target.urlClass === 'dynamic',
                })}
              >
                {URL_CLASS_LABEL[target.urlClass]}
              </span>
            </td>
            <td className="py-0.5">
              <code className="break-all font-mono text-xs">{target.url ?? 'target built at runtime'}</code>
            </td>
            <td className="w-12 py-0.5 text-right">
              <LineLink line={target.evidence.line} onGoToLine={props.onGoToLine} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export function ConfigurationFacts(props: { widget: CodeWidgetAnalysis }) {
  const { configuration } = props.widget
  const { mutationLogs } = configuration

  return (
    <dl className="grid grid-cols-[10rem_1fr] gap-x-3 gap-y-1">
      <Fact label="Query">{configuration.hasQuery ? 'configured' : 'none'}</Fact>
      <Fact label="Writes to graph">{configuration.targetGraph ?? 'none configured'}</Fact>
      <Fact label="May start rules">
        {configuration.allowedRuleTags.length ? configuration.allowedRuleTags.join(', ') : 'none'}
      </Fact>
      <Fact label="Edit logging">
        {mutationLogs.enabled ? `on, into ${mutationLogs.graph ?? 'no graph set'}` : 'off'}
      </Fact>
      {configuration.minHeight && <Fact label="Minimum height">{configuration.minHeight}</Fact>}
      {configuration.declaredConfigFields.length > 0 && (
        <Fact label="Fields from page">
          <ul className="space-y-0.5">
            {configuration.declaredConfigFields.map((field) => (
              <li key={field.name}>
                {field.label} <span className="text-muted-foreground">({field.type})</span>
                {' — '}
                <FieldValues values={field.values} paths={props.widget.paths} />
              </li>
            ))}
          </ul>
        </Fact>
      )}
    </dl>
  )
}

/**
 * One value per placement, numbered to match the paths above. The same page configured in five places is
 * one report, but the settings it is given need not agree. Collapsed to one value when they all agree.
 */
function FieldValues(props: { values: (string | null)[]; paths: string[] }) {
  const shown = (value: string | null) =>
    value == null ? (
      <span className="text-muted-foreground">not set</span>
    ) : (
      <code className="font-mono text-xs">{value}</code>
    )

  if (props.values.every((value) => value === props.values[0])) return shown(props.values[0] ?? null)

  return (
    <ul className="mt-0.5 space-y-0.5">
      {props.values.map((value, index) => (
        <li key={index} className="flex flex-wrap items-baseline gap-x-2">
          <span className="shrink-0 font-mono text-xs text-muted-foreground">{index + 1}.</span>
          {shown(value)}
          {props.paths[index] && (
            <span className="break-all font-mono text-xs text-muted-foreground">on {props.paths[index]}</span>
          )}
        </li>
      ))}
    </ul>
  )
}

function Fact(props: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{props.label}</dt>
      <dd>{props.children}</dd>
    </>
  )
}

export function Block(props: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-4 last:mb-0">
      <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{props.title}</h3>
      {props.children}
    </section>
  )
}
